import { clsx } from 'clsx';
import { createUseStyles } from 'react-jss';
import { useEffect } from 'react';

import type { LcpnTheme } from '../../../styles/theme';
import type { HeatMapCountryMetaData } from '../../../map-data/map-data-types';
import { Icon } from '../../Icon';
import { useFavouritesContext } from '../../../providers/FavouritesProvider';
import { Typography } from '../../Typography';
import { COUNTRY_LIST_SUB_HEADING } from '../constants';
import { getHeatmapCountryListScrollContainer } from '../get-heatmap-country-list-scroll-container';

import { CountryMetaData } from './CountryMetaData';

/* Warning, this has been duplicated from the ProgrammeRow */
const useStyles = createUseStyles((theme: LcpnTheme) => ({
  programmeRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors['grey-55'],
    borderTop: `${theme.colors['white']} solid 2px`,
    padding: theme.spacing(1),
    cursor: 'pointer',
    width: '100%',
  },

  group: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    marginLeft: theme.spacing(2),
    'html[dir="rtl"] &': {
      marginLeft: 0,
      marginRight: theme.spacing(2),
    },
  },

  title: {
    color: theme.colors['green-100'],
  },

  titleDefault: {
    color: theme.colors.default,
  },

  icon: {
    marginRight: theme.spacing(6),
  },

  status: {
    padding: theme.spacing(4, 4, 4, 0),
    'html[dir="rtl"] &': {
      padding: theme.spacing(4, 0, 4, 4),
    },
  },

  listItemWrapper: {
    margin: 0,
  },

  open: {},

  favouriteButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    '&:hover': {
      opacity: 0.7,
    },
  },

  statusGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },

  [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
    programmeRow: {
      '&&': {
        borderBottom: `solid 1px ${theme.colors['grey-100']}`,
        padding: theme.spacing(1, 0),
        marginBottom: 0,
      },
    },
    group: {
      '&&': {
        marginLeft: theme.spacing(4),
      },
    },
    open: {
      '&&': {
        backgroundColor: theme.colors['grey-55'],
      },
    },
    icon: {
      '&&': {
        marginRight: theme.spacing(1),
      },
    },
  },
}));

type Props = {
  active: boolean;
  record: HeatMapCountryMetaData;
  handleClick: () => void;
  countryIndex: number;
};

const COUNTRY_LIST_ITEM_ID_PREFIX = 'country-list-item';

export function CountryRowAccordion({
  active,
  record,
  handleClick,
  countryIndex,
}: Props) {
  const classes = useStyles();
  const { toggleHeatmapFavourite, isHeatmapFavourited } =
    useFavouritesContext();

  useEffect(() => {
    if (!active) return;

    // get list item height
    const listItemHeight =
      document.querySelector<HTMLLIElement>(`#${COUNTRY_LIST_ITEM_ID_PREFIX}-0`)
        ?.offsetHeight ?? 0;

    // get list sub heading height
    const subHeadingHeight =
      document.querySelector<HTMLDivElement>(`#${COUNTRY_LIST_SUB_HEADING}`)
        ?.offsetHeight ?? 0;

    const scrollContainer = getHeatmapCountryListScrollContainer();

    const currentListItemHeightFromTopOfList =
      countryIndex * listItemHeight + subHeadingHeight;

    scrollContainer?.scrollTo({
      behavior: 'smooth',
      top: currentListItemHeightFromTopOfList,
    });
  }, [active, countryIndex]);

  //if (active) return <HeatmapPrintTable country={record} />;
  return (
    <li
      className={classes.listItemWrapper}
      id={`${COUNTRY_LIST_ITEM_ID_PREFIX}-${countryIndex}`}
    >
      <button
        className={clsx(classes.programmeRow, { [classes.open]: active })}
        onClick={handleClick}
      >
        <div className={classes.group}>
          <Icon
            alt={record.countryStatus}
            variant={active ? 'CollapseList' : 'ExpandList'}
            stroke="#6a6a6a"
            fill="#fff"
            width={20}
            height={20}
          />
          <div
            className={clsx(
              { [classes.titleDefault]: !active },
              { [classes.title]: active },
            )}
          >
            <Typography variant="p2">{record.countryName}</Typography>
          </div>
        </div>
        <div className={classes.statusGroup}>
          <div className={classes.status}>
            <Typography variant="p3">{record.countryStatus}</Typography>
          </div>
          <div
            role="button"
            tabIndex={0}
            className={classes.favouriteButton}
            onClick={e => {
              e.stopPropagation();
              toggleHeatmapFavourite(record.countryIso3166_1_2Name);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                toggleHeatmapFavourite(record.countryIso3166_1_2Name);
              }
            }}
            aria-label={
              isHeatmapFavourited(record.countryIso3166_1_2Name)
                ? 'Remove from favourites'
                : 'Add to favourites'
            }
          >
            <Icon
              variant={
                isHeatmapFavourited(record.countryIso3166_1_2Name)
                  ? 'HeartFilled'
                  : 'Heart'
              }
              width={18}
              height={18}
              style={{
                color: isHeatmapFavourited(record.countryIso3166_1_2Name)
                  ? '#e63946'
                  : '#6a6a6a',
              }}
            />
          </div>
        </div>
      </button>
      {active && <CountryMetaData record={record} active={active} />}
    </li>
  );
}
